import React, { useState } from "react";

import {
    Box,
    Typography,
    TextField,
    Autocomplete,
    Chip,
    Modal,
    Card,
    CardContent,
    Button,
    Divider,
} from "@mui/material";

import { useMutation, useQueryClient, useQuery } from "react-query";
import { createTechnician, getUsers } from "../query-functions";

const style = {
    margin: "auto",
    mt: {
        md: 6,
        lg: 8,
        xl: 10,
    },

    bgcolor: "background.paper",

    width: {
        xs: "100%",
        sm: "80%",
        md: "60%",
        lg: "50%",
        xl: "40%",
    },

    minHeight: "200px",
};

const defaultFormErrors = {
    technicianName: false,
    techNameAlreadyExists: false,
};

const CreateTechnicianModal = ({
    technicians,
    clientID,
    open,
    setOpen,
    emitSnack,
}) => {
    const queryClient = useQueryClient();

    const [associatedUser, setAssociatedUser] = useState();
    const [technicianName, setTechnicianName] = useState("");

    const [formErrors, setFormErrors] = useState(defaultFormErrors);

    const { data: users } = useQuery("users", () => getUsers({ clientID }));

    const { mutate: createTech, isLoading: createTechLoading } = useMutation(
        createTechnician,
        {
            onSuccess: () => {
                queryClient.invalidateQueries("technicians");

                emitSnack({
                    type: "success",
                    message: "Technician Created",
                });
                handleModalClose();
            },
        }
    );

    const handleModalClose = () => {
        setFormErrors(defaultFormErrors);
        setAssociatedUser(null);
        setTechnicianName("");
        setOpen(false);
    };

    const handleCreateTechnician = (e) => {
        e.preventDefault();
        const techWithSameName = technicians.find(
            (technician) =>
                technician.techName.trim().toLowerCase() ===
                technicianName.trim().toLowerCase()
        );

        if (technicianName.trim().length === 0 && !associatedUser) {
            setFormErrors({ ...formErrors, technicianName: true });
            return;
        } else if (techWithSameName && !associatedUser) {
            setFormErrors({ ...formErrors, techNameAlreadyExists: true });
            return;
        }

        if (associatedUser) {
            createTech({
                clientID,
                techName:
                    associatedUser.firstName + " " + associatedUser.lastName,
                userId: associatedUser?.userId,
            });
            return;
        }

        // checks if all the form fields are valid to create a technician based on the name input
        if (
            Object.values(formErrors).filter((error) => error === true)
                .length === 0
        ) {
            createTech({
                clientID,
                techName: technicianName,
            });
        }
    };

    return (
        <Modal open={open} onClose={handleModalClose}>
            <Card sx={style}>
                <form onSubmit={handleCreateTechnician}>
                    <CardContent>
                        <Typography variant="h6">
                            Create New Technician
                        </Typography>

                        <TextField
                            label="Technician Name"
                            variant="outlined"
                            size="small"
                            sx={{ mt: 1.5, width: "100%" }}
                            required={!associatedUser}
                            value={technicianName}
                            onChange={(e) => {
                                setTechnicianName(e.target.value);

                                setFormErrors({
                                    ...formErrors,
                                    technicianName: false,
                                });

                                if (associatedUser) {
                                    setAssociatedUser(null);
                                }
                            }}
                            onFocus={() =>
                                setFormErrors({
                                    ...formErrors,
                                    technicianName: false,
                                    techNameAlreadyExists: false,
                                })
                            }
                            error={
                                formErrors.technicianName ||
                                formErrors.techNameAlreadyExists
                            }
                            helperText={
                                formErrors.technicianName
                                    ? "Technician name is required"
                                    : formErrors.techNameAlreadyExists
                                    ? "Technician name already exists"
                                    : ""
                            }
                        />

                        <Typography variant="subtitle1" sx={{ mt: 1.5 }}>
                            Or associate with an existing user
                        </Typography>

                        <Autocomplete
                            sx={{ mt: 1.5 }}
                            size="small"
                            value={
                                associatedUser
                                    ? associatedUser.firstName +
                                      " " +
                                      associatedUser.lastName
                                    : null
                            }
                            onChange={(ev, newValue) => {
                                if (technicianName) {
                                    setTechnicianName("");
                                }

                                setAssociatedUser(
                                    users.find(
                                        (user) =>
                                            user.firstName +
                                                " " +
                                                user.lastName ===
                                            newValue
                                    )
                                );
                            }}
                            options={
                                users
                                    ? users
                                          .filter(
                                              (user) => user.techID === null
                                          )
                                          .map(
                                              (user) =>
                                                  user.firstName +
                                                  " " +
                                                  user.lastName
                                          )
                                    : []
                            }
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        variant="outlined"
                                        label={option}
                                        {...getTagProps({
                                            index,
                                        })}
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="filled"
                                    label="Associated User"
                                />
                            )}
                        />
                    </CardContent>
                    <Divider />
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            p: 2,
                        }}
                    >
                        <Button sx={{ mr: 1 }} onClick={handleModalClose}>
                            Close
                        </Button>
                        <Button
                            variant="contained"
                            type="submit"
                            disabled={
                                formErrors.technicianName ||
                                formErrors.techNameAlreadyExists ||
                                createTechLoading ||
                                (!technicianName && !associatedUser) ||
                                false
                            }
                        >
                            Create
                        </Button>
                    </Box>
                </form>
            </Card>
        </Modal>
    );
};

export default CreateTechnicianModal;
